import Header from '@/components/Header'
import useDocumentTitle from '@/components/Helmet';
import { useCustomLoader } from '@/components/Loader';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance';
import { formatDate, formatPrice, formatTableDate } from '@/lib/utils';
import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Card, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { Phone, Trash } from 'lucide-react';
import React, { useEffect, useState } from 'react'

const Inquires = () => {
    useDocumentTitle("Inquires");
    const loader = useCustomLoader();
    const { toast } = useToast();
    const isMobile = useMediaQuery("(max-width: 600px)");

    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [count, setCount] = useState(0);
    const [inquires, setInquires] = useState([]);
    const [expanded, setExpanded] = React.useState<string | false>(false);

    const [openModal, setOpenModal] = useState(null);

    const handleClose = () => setOpenModal(null);

    const fetchInquires = async () => {
        loader(true);
        try {
            await axiosInstance.get(`/api/inquiries?limit=${limit}&page=${page + 1}`)
                .then((res) => {
                    setInquires(res.data.data);
                    setCount(res.data.count);
                })
        } catch (error: any) {
            console.log(error)
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        }
        finally {
            loader(false);
        }
    }

    useEffect(() => {
        fetchInquires();
    }, [page, limit])

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/api/inquiries/${openModal}`)
                .then((res) => {
                    if (res.data?.success) {
                        fetchInquires();
                        handleClose();
                        toast({
                            variant: "success",
                            title: "Success!",
                            description: "Inquiry deleted successfully",
                        })
                    }
                    else {
                        toast({
                            variant: "destructive",
                            title: "Error!",
                            description: res?.data?.message || "Something went wrong",
                        })
                    }
                })
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        }
    }
    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Paper elevation={0} sx={{p : 2}}>
                    <h2 className="text-2xl font-semibold mb-4">Inquiries</h2>
                    <TableContainer>
                        {isMobile ? <Box>
                            {
                                inquires?.map((inquery: any, index: any) => (
                                    <Accordion
                                        expanded={expanded === inquery?._id}
                                        key={inquery?._id}
                                        onChange={() => setExpanded(expanded === inquery?._id ? null : inquery?._id)}
                                        className='mb-2'
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            aria-controls="panel1-content"
                                            id="panel1-header"
                                            sx={{
                                                "& .MuiAccordionSummary-content": {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                },
                                                backgroundColor: "#f9fafb",
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Avatar src={inquery?.seller?.avatar} />
                                                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                                                    {inquery?.name}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={inquery?.status}
                                                size="small"
                                                sx={{ ml: "auto", width: 100, height: 30, textTransform: "capitalize" }}
                                                color={inquery?.status ? "success" : "error"}
                                            />
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Email:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.email}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Phone:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.phone}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Prop. Title:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.property?.title}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Prop. Price:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{formatPrice(inquery?.property?.price)}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Prop. Adress:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.property?.address}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Prop. City:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.property?.city}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Agent Name:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.property?.agent?.name}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Agent Email:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.property?.agent?.email}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Agent Phone:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.property?.agent?.phone}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Agent Company:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{inquery?.property?.agent?.company}</span>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <span >Created At:</span><br />
                                                    <span className="font-semibold break-words overflow-hidden text-ellipsis block">{formatTableDate(inquery?.createdAt)}</span>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <span >message:</span><br />
                                                    <span className="font-semibold">
                                                        {inquery?.message || "N/A"}
                                                    </span>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent : "center", gap: 2 }}>
                                                        <Tooltip title="Call" placement='top'>
                                                            <a href={`tel:${inquery.phone}`}><Phone className='text-green-600' /></a>
                                                        </Tooltip>
                                                        <Tooltip title="Delete" placement='top'>
                                                            <Trash className='text-red-600' onClick={() => setOpenModal(inquery._id)} />
                                                        </Tooltip>
                                                    </Box>
                                                </Grid>

                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            }
                        </Box> : <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Prop. Name</TableCell>
                                    <TableCell>Prop. City</TableCell>
                                    <TableCell>Prop. Price</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone</TableCell>
                                    {/* <TableCell>Message</TableCell> */}
                                    <TableCell>Date</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    inquires.map((inquire: any) => (
                                        <TableRow key={inquire.id} className='hover:bg-gray-100 cursor-pointer'>
                                            <TableCell>{inquire.property?.title}</TableCell>
                                            <TableCell>{inquire.property?.city}</TableCell>
                                            <TableCell>{formatPrice(inquire.property?.price)}</TableCell>
                                            <TableCell>{inquire.name}</TableCell>
                                            <TableCell>{inquire.email}</TableCell>
                                            <TableCell>{inquire.phone}</TableCell>
                                            {/* <TableCell>{inquire.message.slice(0, 40)}...</TableCell> */}
                                            <TableCell>{formatDate(inquire.createdAt)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", gap: 2 }}>
                                                    <Tooltip title="Call" placement='top'>
                                                        <a href={`tel:${inquire.phone}`}><Phone className='text-green-600' /></a>
                                                    </Tooltip>
                                                    <Tooltip title="Delete" placement='top'>
                                                        <Trash className='text-red-600' onClick={() => setOpenModal(inquire._id)} />
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                                {
                                    !inquires.length && (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ textAlign: "center", color: "red" }}>No inquiries found</TableCell>
                                        </TableRow>
                                    )
                                }
                            </TableBody>
                        </Table>}
                        <TablePagination
                            component="div"
                            count={count}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            rowsPerPage={limit}
                            onRowsPerPageChange={(event) => {
                                setLimit(parseInt(event.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </TableContainer>
                </Paper>

                <Dialog
                    open={!!openModal}
                    // slots={{
                    //     transition: Transition,
                    // }}
                    keepMounted
                    onClose={handleClose}
                    aria-describedby="alert-dialog-slide-description"
                >
                    <DialogTitle>{"Are you sure want to delete Inquiry?"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-slide-description">
                            This action cannot be undone
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Button onClick={handleClose} variant='default'>Cancel</Button>
                        <Button onClick={handleDelete} variant='error'>Yes, Delete It</Button>
                    </DialogActions>
                </Dialog>
            </section>
        </div>
    )
}

export default Inquires